import Roles, { RoleGroups } from '@/constants/Roles';
import StorageKeys from '@/constants/StorageKeys';
import { TransportType } from '@/constants/TransportType';
import type { IProfile } from '@/types/interfaces/AccountTypes';
import type { IJwt } from '@/types/interfaces/LoginTypes';
import type { ITransportListResponse } from '@/types/interfaces/TransportTypes';
import { fromUnixTime } from 'date-fns';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAuthStore = defineStore('auth', () => {
    const defaultUser: IProfile = {
        displayName: '',
        roles: [],
        station: '',
        stations: [],
        username: '',
        expires: new Date(0)
    };

    const user = ref<IProfile>(defaultUser);

    // Mirror the server's UserContextHelper.Is*Only: the role is the user's one and only role.
    const isDriverOnly = computed(() => isOnly(Roles.Drivers));
    const isMailOnly = computed(() => isOnly(Roles.Mail));
    const isCargoOfficeOnly = computed(() => isOnly(Roles.CargoOffice));

    // Keep in sync with the .RequireRole(...) on the matching server endpoints.
    const canEditTransports = computed(() => hasRole(RoleGroups.Transports));
    const canEditDrives = computed(() => hasRole([ Roles.Administrators, Roles.StationAdmins, Roles.CargoControl ]));
    const canEditUlds = computed(() => hasRole([ Roles.Administrators, Roles.StationAdmins, Roles.CargoControl, Roles.Mail ]));
    const canEditAwbs = computed(() => hasRole([ Roles.Administrators, Roles.StationAdmins, Roles.CargoControl ]));
    // Keep in sync with drives/list and awbs/list.
    const canViewDrivesAndAwbs = computed(() => hasRole([ Roles.Administrators, Roles.StationAdmins, Roles.CargoControl, Roles.CargoOffice ]));

    // Keep in sync with PUT transports/{id}: mail-only users only change mail transports, Cargo Office-only users only transfers.
    function canEditTransport(transport: Pick<ITransportListResponse, 'mailOnly' | 'transportType'>): boolean {
        return canEditTransports.value
            && (transport.mailOnly === true || !isMailOnly.value)
            && (transport.transportType === TransportType.Transfer || !isCargoOfficeOnly.value);
    }

    // Keep in sync with the cancel, enable and unit-count endpoints.
    function canChangeTransportStatus(transport: Pick<ITransportListResponse, 'mailOnly'>): boolean {
        return hasRole([ Roles.Administrators, Roles.StationAdmins, Roles.CargoControl, Roles.Mail ])
            && (transport.mailOnly === true || !isMailOnly.value);
    }

    function initialize(token?: string): boolean {
        if (!token) {
            clear();
            return false;
        }

        return parseToken(token);
    }

    function clear(): void {
        localStorage.removeItem(StorageKeys.Token);
        user.value = { ...defaultUser };
    }

    /**
     * A malformed token must not throw: parseToken runs inside the router guard, and an
     * exception there aborts navigation and leaves the app on a blank page.
     */
    function parseToken(token: string): boolean {
        try {
            const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            const jwt: IJwt = JSON.parse(jsonPayload) as IJwt;
            user.value = {
                displayName: jwt.displayname,
                roles: jwt.roles,
                station: jwt.station,
                stations: jwt.stations,
                username: jwt.name,
                expires: fromUnixTime(jwt.exp),
                mfaPending: typeof jwt.mfa !== 'undefined'
            };

            localStorage.setItem(StorageKeys.Token, token);
            return true;
        } catch (error) {
            console.error('Unable to parse the stored token, clearing it.', error);
            clear();
            return false;
        }
    }

    function isAuthenticated(): boolean {
        const token = localStorage.getItem(StorageKeys.Token);
        if (token === null) {
            return false;
        }

        // The MFA half-token is kept (the MFA pages send it) but is no login: the server only accepts it there.
        const parsed = user.value.username === '' ? initialize(token) : true;
        return parsed && !user.value.mfaPending;
    }

    function isOnly(role: string): boolean {
        return user.value?.roles?.length === 1 && hasRole(role);
    }

    function hasRole(role: string | readonly string[]): boolean {
        if (!user.value || !user.value.roles) {
            return false;
        }

        return typeof role === 'string'
            ? user.value.roles.includes(role)
            : role.some(x => user.value.roles.includes(x));
    }

    return {
        initialize, isAuthenticated, hasRole, user,
        isDriverOnly, isMailOnly, isCargoOfficeOnly, canEditTransports, canEditDrives, canEditUlds, canEditAwbs, canViewDrivesAndAwbs,
        canEditTransport, canChangeTransportStatus
    };
});